import React, { useState, useEffect } from 'react';
import { FaLongArrowAltLeft } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import ApplicationService from '../services/axios.service'; // Assuming this service has the necessary method

const ViewForecastHistory = () => {
    const { id } = useParams(); // Get transaction ID from URL params
    const [errorDetails, setErrorDetails] = useState([]); // State for the errors
    const [loading, setLoading] = useState(true);
    const [successDetails, setSuccessDetails] = useState([]); // State for the success records
    const [currentErrorPage, setCurrentErrorPage] = useState(1); // Current error page
    const [currentSuccessPage, setCurrentSuccessPage] = useState(1); // Current success page
    const recordsPerPage = 10; // Number of records to show per page
    const navigate = useNavigate();
    const service = new ApplicationService();

    // Fetch error or success details for the given transaction ID
    const fetchDetails = async () => {
        try {
            const response = await service.viewForecastHistory(id); // Assuming the method is defined in ApplicationService
            if (response.status === 200) {
                if (response?.data?.status === 'errored') {
                    const errors = JSON.parse(response?.data?.details); // Parse the details field into an array of errors
                    setErrorDetails(errors);
                } else if (response?.data?.status === 'success') {
                    const records = JSON.parse(response?.data?.details); // Parse the details field into an array of records
                    setSuccessDetails(records);
                }
            } else {
                console.error("Error fetching details:", response.message);
            }
        } catch (error) {
            console.error("Error fetching details:", error);
        } finally {
            setLoading(false);
        }
    };

    // UseEffect to fetch the data when the component mounts
    useEffect(() => {
        fetchDetails();
    }, [id]);

    // Error pagination logic
    const totalErrorPages = Math.ceil(errorDetails.length / recordsPerPage); // Total error pages
    const indexOfLastError = currentErrorPage * recordsPerPage;
    const indexOfFirstError = indexOfLastError - recordsPerPage;
    const currentErrorRecords = errorDetails.slice(indexOfFirstError, indexOfLastError);

    // Success pagination logic
    const totalSuccessPages = Math.ceil(successDetails.length / recordsPerPage); // Total success pages
    const indexOfLastSuccess = currentSuccessPage * recordsPerPage;
    const indexOfFirstSuccess = indexOfLastSuccess - recordsPerPage;
    const currentSuccessRecords = successDetails.slice(indexOfFirstSuccess, indexOfLastSuccess);

    // Paginate error records
    const paginateError = (pageNumber) => setCurrentErrorPage(pageNumber);

    // Paginate success records
    const paginateSuccess = (pageNumber) => setCurrentSuccessPage(pageNumber);

    return (
        <div className="bg-white border border-[#ccc] p-4">
            <button
                className="bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 cursor-pointer"
                onClick={() => navigate("/bulk-upload-history")}
            >
                <span className="text-white flex flex-row">
                    <FaLongArrowAltLeft /> &nbsp; Back
                </span>
            </button>

            <div className="container mx-auto p-4">
                {/* Header */}
                <div className="flex justify-between items-center border-b pb-4">
                    <h1 className="text-2xl font-semibold">Bulk Upload Details for Transaction ID: {id}</h1>
                </div>

                {/* Success/Error Info */}
                <div className="my-4">
                    <p className="text-lg text-blue-800">Total Records in the Uploaded file: {successDetails.length + errorDetails.length}</p>
                    <p className="text-lg text-red-600">Total Records with Errors: {errorDetails.length}</p>
                    <p className="text-lg text-green-600">Total Successful Records: {successDetails.length}</p>
                </div>

                <div className="my-4 text-gray-700">
                    <p className="font-medium text-lg">Check the root cause of the errors below. Please correct them and import again.</p>
                </div>

                {/* Error Table */}
                {errorDetails.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-gray-600">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-4 py-2 text-left">Row</th>
                                    <th className="px-4 py-2 text-left">Model</th>
                                    <th className="px-4 py-2 text-left">Options</th>
                                    <th className="px-4 py-2 text-left">Budget</th>
                                    <th className="px-4 py-2 text-left">Forecast</th>
                                    <th className="px-4 py-2 text-left">Next Forecast</th>
                                    <th className="px-4 py-2 text-left">Error</th> {/* Added Error column */}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4">Loading...</td>
                                    </tr>
                                ) : (
                                    currentErrorRecords.map((error, index) => (
                                        <tr key={index} className="border-b hover:bg-gray-50">
                                            <td className="px-4 py-2">{error.row}</td>
                                            <td className="px-4 py-2">{error.record.model}</td>
                                            <td className="px-4 py-2">{error.record.options}</td>
                                            <td className="px-4 py-2">{error.record.budget}</td>
                                            <td className="px-4 py-2">{error.record.forecast}</td>
                                            <td className="px-4 py-2">{error.record.next_forecast}</td>
                                            <td className="px-4 py-2 text-red-600">{error.error}</td> {/* Render the error message */}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                        {/* Pagination for Errors */}
                        <div className="flex justify-center mt-4">
                            <button
                                onClick={() => paginateError(currentErrorPage - 1)}
                                disabled={currentErrorPage === 1}
                                className="px-4 py-2 bg-gray-300 text-white rounded-md cursor-pointer mr-2 disabled:opacity-50"
                            >
                                &lt; Previous
                            </button>
                            <span className="px-4 py-2 text-gray-700">
                                Page {currentErrorPage} of {totalErrorPages}
                            </span>
                            <button
                                onClick={() => paginateError(currentErrorPage + 1)}
                                disabled={currentErrorPage === totalErrorPages}
                                className="px-4 py-2 bg-gray-300 text-white rounded-md cursor-pointer ml-2 disabled:opacity-50"
                            >
                                Next &gt;
                            </button>
                        </div>
                    </div>
                )}

                {/* Success Records Table */}
                {successDetails.length > 0 && (
                    <div className="overflow-x-auto mt-6">
                        <table className="min-w-full text-sm text-gray-600">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-4 py-2 text-left">Model</th>
                                    <th className="px-4 py-2 text-left">Options</th>
                                    <th className="px-4 py-2 text-left">Budget</th>
                                    <th className="px-4 py-2 text-left">Forecast</th>
                                    <th className="px-4 py-2 text-left">Next Forecast</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentSuccessRecords.map((record, index) => (
                                    <tr key={index} className="border-b hover:bg-gray-50">
                                        <td className="px-4 py-2">{record.model}</td>
                                        <td className="px-4 py-2">{record.options}</td>
                                        <td className="px-4 py-2">{record.budget}</td>
                                        <td className="px-4 py-2">{record.forecast}</td>
                                        <td className="px-4 py-2">{record.next_forecast}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {/* Pagination for Success Records */}
                        <div className="flex justify-center mt-4">
                            <button
                                onClick={() => paginateSuccess(currentSuccessPage - 1)}
                                disabled={currentSuccessPage === 1}
                                className="px-4 py-2 bg-gray-300 text-white rounded-md cursor-pointer mr-2 disabled:opacity-50"
                            >
                                &lt; Previous
                            </button>
                            <span className="px-4 py-2 text-gray-700">
                                Page {currentSuccessPage} of {totalSuccessPages}
                            </span>
                            <button
                                onClick={() => paginateSuccess(currentSuccessPage + 1)}
                                disabled={currentSuccessPage === totalSuccessPages}
                                className="px-4 py-2 bg-gray-300 text-white rounded-md cursor-pointer ml-2 disabled:opacity-50"
                            >
                                Next &gt;
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewForecastHistory;
